import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '@forbiz/web-kit'
import type { CatalogItem } from '@forbiz/contracts'

import { ProductInfo } from '../components/ProductInfo.js'
import { isFavorite, toggleFavorite } from '../lib/favorites.js'
import { ordersApi } from '../lib/orders.js'
import { pointsApi } from '../lib/points.js'
import { defaultProductImage } from '../lib/productImage.js'
import { useAsync } from '../lib/useAsync.js'
import s from './ProductDetailPage.module.css'

/**
 * 상품 상세 (BIZ-103). 카탈로그/홈에서 상품을 누르면 여기로 온다(진입점 결정 = a).
 * 신뢰정보(규격·구성·배송·유의사항)를 보여주고 "이 상품으로 주문"으로 주문흐름에 합류한다
 * (유형·상품 프리필 → 상품 선택 단계 스킵). 즐겨찾기는 기기별 로컬 별(★).
 *
 * 배송비·환불은 아직 상품 데이터에 없어 정책 정적 텍스트로 둔다(후속 데이터화).
 */

export function ProductDetailPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const productId = id ? Number(id) : null
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const companyId = user?.companyId ?? null

  const { data, loading } = useAsync(
    () => (isCompany && companyId ? ordersApi.catalog(companyId) : Promise.resolve({ items: [] })),
    [isCompany, companyId],
  )
  // 포인트 율(BIZ-74) — 정가 아래 즉시할인가·적립을 함께 보여준다. 실패하면 정가만.
  const pointSummary = useAsync(
    () => (isCompany && companyId ? pointsApi.summary().catch(() => null) : Promise.resolve(null)),
    [isCompany, companyId],
  )
  const product: CatalogItem | undefined = useMemo(
    () => data?.items.find((p) => p.productId === productId),
    [data, productId],
  )
  // 같은 유형의 다른 상품 — "2단이냐 3단이냐" 비교가 화환 구매의 실제 고민이라 상세에서 바로 잇는다.
  const similar = useMemo(
    () =>
      product
        ? (data?.items ?? []).filter(
            (p) => p.occasionType === product.occasionType && p.productId !== product.productId,
          )
        : [],
    [data, product],
  )

  // 즐겨찾기 — 비슷한 상품으로 이동하면 같은 라우트라 리마운트가 안 되므로 상품별로 다시 읽는다.
  const [favV, setFavV] = useState(0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fav = useMemo(() => (productId != null ? isFavorite(productId) : false), [productId, favV])
  // 비슷한 상품으로 넘어갈 때 스크롤을 위로 — 아래쪽 그대로면 페이지가 안 바뀐 것처럼 보인다.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [productId])

  if (user && !isCompany) return <Navigate to="/orders" replace />

  const order = () => {
    if (!product) return
    navigate('/orders/new', {
      state: { presetProduct: { occasionType: product.occasionType, productId: product.productId } },
    })
  }
  const onStar = () => {
    if (productId == null) return
    toggleFavorite(productId)
    setFavV((v) => v + 1)
  }
  // 뒤로가기 — 앱 안 히스토리가 있으면 보던 곳(탭 상태 포함)으로, 직접 진입이면 목록으로.
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate('/products')
  }

  return (
    <div className={s.page}>
      <button type="button" onClick={goBack} className={s.back}>
        ← 상품 목록
      </button>

      {loading ? (
        <p className={s.muted}>불러오는 중…</p>
      ) : !product ? (
        <p className={s.muted}>상품을 찾을 수 없습니다.</p>
      ) : (
        <>
          <div className={s.heroWrap}>
            <div
              className={s.hero}
              style={{ backgroundImage: `url(${product.imageUrl ?? defaultProductImage})` }}
              role="img"
              aria-label={product.name}
            />
            <button
              type="button"
              className={fav ? `${s.star} ${s.starOn}` : s.star}
              aria-pressed={fav}
              aria-label={fav ? '즐겨찾기 해제' : '즐겨찾기'}
              title={fav ? '즐겨찾기 해제' : '즐겨찾기'}
              onClick={onStar}
            >
              {fav ? '★' : '☆'}
            </button>
          </div>

          <ProductInfo product={product} rates={pointSummary.data} />

          {/* 같은 유형 다른 상품 — 목록으로 돌아가지 않고 가격대·단수를 바로 비교한다. */}
          {similar.length > 0 ? (
            <section className={s.sim}>
              <h2 className={s.simTitle}>비슷한 상품</h2>
              <div className={s.simRow}>
                {similar.map((p) => (
                  <button
                    key={p.productId}
                    type="button"
                    className={s.simCard}
                    onClick={() => navigate(`/products/${p.productId}`)}
                  >
                    <span
                      className={s.simIm}
                      style={{ backgroundImage: `url(${p.imageUrl ?? defaultProductImage})` }}
                      aria-hidden="true"
                    />
                    <span className={s.simName}>{p.name}</span>
                    <span className={s.simPrice}>{p.price.toLocaleString('ko-KR')}원</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <div className={s.footer}>
            <div className={s.footerInner}>
              <button type="button" className={`btn ${s.cta}`} onClick={order}>
                이 상품으로 주문
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
